import PropTypes from "prop-types";
import { useContext, useMemo, useState } from "react";
import { AppContext } from "../../../pages/root/Root";

import Grid from "@mui/material/Unstable_Grid2";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Autocomplete,
  createFilterOptions,
} from "@mui/material/";

import { Search } from "@mui/icons-material";

import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers/";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import useBuyerStatus from "../../../hooks/eRetail/useBuyerStatus";
import useBuyersAdPaid from "../../../hooks/eRetail/useBuyersAdPaid";

import { initLocalization } from "../../../utils/helpers";
import { dataAssignmentFiltersStrings } from "./locale";
import {
  MAPPING_KEYS_API_DATE_FORMAT,
  START_DATE,
  END_DATE,
  MAPPING_KEYS_DATE_FORMAT,
  SEARCH_FIELD_MIN_CHARS,
} from "../../../constants";

const filterOptions = createFilterOptions();

export const DataAssignmentFilters = ({
  filters,
  setFilters,
  exclusions,
  setExclusions,
  onSearch,
}) => {
  initLocalization(dataAssignmentFiltersStrings);

  const { language } = useContext(AppContext);

  const { statuses } = useBuyerStatus(true);
  const { isLoading: exclusionsLoading, adExclusions } = useBuyersAdPaid(true);

  const [selectedExclusions, setSelectedExclusions] = useState([]);

  const allExclusionsEntry = useMemo(
    () => [dataAssignmentFiltersStrings.statuses.All],
    [language],
  );

  const adExclusionsList = useMemo(() => {
    const sorted = [...adExclusions].sort((a, b) => {
      let ai = selectedExclusions.indexOf(a);
      ai = ai === -1 ? selectedExclusions.length + adExclusions.indexOf(a) : ai;
      let bi = selectedExclusions.indexOf(b);
      bi = bi === -1 ? selectedExclusions.length + adExclusions.indexOf(b) : bi;
      return ai - bi;
    });

    if (!sorted.length) {
      return [];
    }

    return [...allExclusionsEntry, ...sorted];
  }, [adExclusions, selectedExclusions, allExclusionsEntry]);

  const disableSearch = () => {
    return !filters.status;
  };

  const handleSetFilters = (event) => {
    setFilters((prev) => {
      let next = {
        ...prev,
        [event.target.name]: event.target.value,
      };

      return next;
    });
  };

  const handleDateChange = (value, name) => {
    setFilters({
      ...filters,
      [name]: dayjs(value).format(MAPPING_KEYS_API_DATE_FORMAT),
    });
  };

  const handleFieldKeydown = (event) => {
    if (disableSearch()) {
      return;
    }

    if (event.key === "Enter" && event.target.value) {
      event.stopPropagation();
      onSearch();
    }
  };

  const handleExclusionsChange = (event, newValues, reason) => {
    if (
      event.type === "keydown" &&
      (event.key === "Backspace" || event.key === "Delete") &&
      reason === "removeOption"
    ) {
      return;
    }

    if (
      reason === "clear" ||
      newValues.includes(dataAssignmentFiltersStrings.statuses.All)
    ) {
      setSelectedExclusions([]);
    }

    setExclusions({
      advertiserExclusion: {
        advertiserExclusionNames: newValues.includes(
          dataAssignmentFiltersStrings.statuses.All,
        )
          ? [dataAssignmentFiltersStrings.statuses.All]
          : newValues,
        includeAll: newValues.includes(
          dataAssignmentFiltersStrings.statuses.All,
        ),
      },
    });
  };

  const handleExclusionsClose = () => {
    setSelectedExclusions(
      exclusions.advertiserExclusion.advertiserExclusionNames,
    );
  };

  return (
    <Grid container>
      <Grid
        xs={12}
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        gap={2}
      >
        <Box
          sx={{
            "&": {
              flex: "1",
            },
          }}
        >
          <FormControl sx={{ width: "100%" }} size="small">
            <InputLabel id="view-label">
              {dataAssignmentFiltersStrings.status}
            </InputLabel>
            <Select
              labelId="status-label"
              id="status-select"
              value={filters.status}
              label="Status"
              onChange={handleSetFilters}
              name="status"
            >
              <MenuItem key="all" value="All">
                {dataAssignmentFiltersStrings.statuses.All}
              </MenuItem>
              {statuses.map((status) => (
                <MenuItem key={status.name} value={status.name}>
                  {dataAssignmentFiltersStrings.statuses[status.name]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            "&": { flex: 1 },
            "& .MuiTextField-root": { width: "100%" },
          }}
        >
          <TextField
            label={dataAssignmentFiltersStrings.adPaid}
            size="small"
            name="advertiserPaid"
            value={filters.advertiserPaid}
            onChange={handleSetFilters}
            onKeyDown={handleFieldKeydown}
          />
        </Box>

        <Box
          sx={{
            "&": { flex: 1 },
            "& .MuiTextField-root": { width: "100%" },
          }}
        >
          <Autocomplete
            id="ad-paid-exclusions"
            size="small"
            value={exclusions.advertiserExclusion.advertiserExclusionNames}
            options={adExclusionsList}
            loading={exclusionsLoading}
            filterOptions={(options, params) => {
              if (params.inputValue.length < SEARCH_FIELD_MIN_CHARS) {
                return options;
              }

              return filterOptions(options, params);
            }}
            renderOption={(props, option, state) => {
              const { key, ...optionProps } = props;
              return (
                <li
                  key={key}
                  {...optionProps}
                  {...(option === dataAssignmentFiltersStrings.statuses.All
                    ? { style: { borderBottom: "1px solid #ddd" } }
                    : {})}
                >
                  <Checkbox checked={state.selected} />
                  {option}
                </li>
              );
            }}
            renderInput={(params) => {
              const selection =
                exclusions.advertiserExclusion.advertiserExclusionNames;
              return (
                <TextField
                  {...params}
                  label={
                    selection.length
                      ? selection.join(", ")
                      : dataAssignmentFiltersStrings.adPaidTextExclusions
                  }
                />
              );
            }}
            renderTags={() => null}
            getOptionDisabled={(option) =>
              option !== dataAssignmentFiltersStrings.statuses.All &&
              exclusions.advertiserExclusion.includeAll
            }
            onChange={handleExclusionsChange}
            onClose={handleExclusionsClose}
            multiple
            disablePortal
            disableCloseOnSelect
          />
        </Box>

        <Box
          sx={{
            "&": { flex: 1 },
            "& .MuiTextField-root": { width: "100%" },
          }}
        >
          <TextField
            label={dataAssignmentFiltersStrings.adName}
            size="small"
            name="advertiserName"
            value={filters.advertiserName}
            onChange={handleSetFilters}
            onKeyDown={handleFieldKeydown}
          />
        </Box>

        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={language}
        >
          <DatePicker
            label={dataAssignmentFiltersStrings.startDate}
            format={MAPPING_KEYS_DATE_FORMAT}
            value={filters.startDate}
            maxDate={
              dayjs(filters[END_DATE]).isValid()
                ? dayjs(filters[END_DATE])
                : dayjs(new Date())
            }
            onChange={(value) => handleDateChange(value, START_DATE)}
            slotProps={{ textField: { size: "small" } }}
            sx={{
              "&": { flex: "1" },
              "& .MuiFormLabel-root": { maxWidth: "calc(100% - 2.875rem)" },
              "& .MuiFormLabel-root.MuiFormLabel-filled, & .MuiFormLabel-root.Mui-focused":
                {
                  maxWidth: "calc(133% - 2rem)",
                },
            }}
          />

          <DatePicker
            label={dataAssignmentFiltersStrings.endDate}
            format={MAPPING_KEYS_DATE_FORMAT}
            value={filters.endDate}
            minDate={
              dayjs(filters[START_DATE]).isValid()
                ? dayjs(filters[START_DATE])
                : null
            }
            maxDate={dayjs(new Date())}
            onChange={(value) => handleDateChange(value, END_DATE)}
            slotProps={{ textField: { size: "small" } }}
            sx={{
              "&": { flex: "1" },
              "& .MuiFormLabel-root": { maxWidth: "calc(100% - 2.875rem)" },
              "& .MuiFormLabel-root.MuiFormLabel-filled, & .MuiFormLabel-root.Mui-focused":
                {
                  maxWidth: "calc(133% - 2rem)",
                },
            }}
          />
        </LocalizationProvider>

        <Button
          startIcon={<Search />}
          variant="contained"
          color="secondary"
          onClick={onSearch}
          disabled={disableSearch()}
        >
          {dataAssignmentFiltersStrings.search}
        </Button>
      </Grid>
    </Grid>
  );
};

DataAssignmentFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  exclusions: PropTypes.object.isRequired,
  setExclusions: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
};
